/**
 * Get a logger object with `error()`, `warn`, `info`, `debug` and `log()` functions, which
 * - print to browser console when called in the browser in a dev environment.
 * - do nothing when called in the browser in a non-dev environment (unless the the local-storage has "nw.logging.level.global" set)
 * - print using console.log to the server console when called on the server in development mode.
 * - output JSON to the server console when called on the server in non-development mode.
 *
 * This logger is smart enough to distinguish between server-side/client-side and dev/prod execution.
 *
 * - All output contains a timestamp in the iso format.
 * - All output contains the logger name to simplify filtering using dev-tools or kibana.
 * - When NODE_ENV=debug, we will print simple console.foo messages on the console on both server side and client side.
 * - When NODE_ENV!=debug
 *     - on client-side
 *         - We will never log anything unless overridden using a local-storage flag.
 *     - on server-side
 *         - We will not print logger.debug messages on the server-side unless you change the global log level by calling
 *          `setLoggingLevel()`; that's intended to experiment with turning logging off/on based on (say) cookies.
 *         - All output will be in a JSON format that our logstash can pick up.
 *         - All output will have the right log level field set so that you can filter on that.
 *
 * @example
 * ```
 * import { getLogger } from '@nerdwallet/js-logging';
 *
 * const logger = getLogger('<my-module>'); // this will cause all your log messages to be tagged with <my-module>
 * // Note that logger.(log|debug|error|warn|info) all have the signature as console.log.
 * logger.debug('foo');                                  // [iso time][<my-module>] foo
 * logger.debug('I was called with', { arg1, arg2 });    // [iso time][<my-module>] I was called with >{ arg1 … }
 * logger.error('This here error', err);                 // [iso time][<my-module>] This here error > { message… }
 *
 * // You can also create child loggers for each function in a module (say a logger for each action in redux)
 * const rootLogger = getLogger('actions');
 * const someLongAction = (arg1, arg2, arg3) =>
 *   async (dispatch) => {
 *     const logger = getLogger('someLongAction', rootLogger);
 *     logger.debug('called with', arg1, arg2, arg3);    // [iso time][actions > someLongAction] called with arg1 arg2 arg3
 *     try {
 *       await someThing();
 *     } catch (err) {
 *       logger.error(err);                              // [iso time][actions > someLongAction] >{ object containing error message, stack trace etc.}
 *     }
 *   }
 * ```
 */
import { logInner } from './lib/logInner';
import { LogTags } from './lib/tags';
import { Level } from './constants';
export { setSentryClient } from './sentry';
export { setLoggingLevel, getLoggingLevel } from './lib/loggingLevel';
export { Level };
const getCallerClientID = (req) => { var _a, _b, _c; 
// look for client_id, first in headers, fallback to query params
return (_b = (_a = req === null || req === void 0 ? void 0 : req.headers) === null || _a === void 0 ? void 0 : _a['x-caller-client-id']) !== null && _b !== void 0 ? _b : (_c = req === null || req === void 0 ? void 0 : req.query) === null || _c === void 0 ? void 0 : _c.client_id; };
/**
 * @param loggerName - name of the logger. This will be prefixed to the output.
 * @param options - Either a parent logger instance, or an options object
 * @param {object} options.serverRequest  - the request object. If provided, will be used to supply tags to the log.
 * @param {string} options.parentLogger - a parent logger instance. If supplied, the new logger will have a logger name which incorporates the parent's name.
 *
 * @example
 * ```
 * import { getLogger } from '@nerdwallet/logging';
 * const logger = getLogger('my-awesome-action-creator');
 * ...
 * ...
 * try {
 *  throw('something broke');
 * } catch (err) {
 *  logger.error(err);
 * }
 * ```
 *
 * This will output to the console the following
 * ```
 * iso time string [my-awesome-action-creator] Error: something broke
 *     at vendor.bundle.blah:3784637643764
 *     at ugirugdj...
 * ```
 */
export const getLogger = (loggerName, options = {}) => {
    let argOptions;
    // options is the parentLogger
    if (options === null || options === void 0 ? void 0 : options.getName) {
        argOptions = {
            parentLogger: options,
        };
    }
    else {
        argOptions = Object.assign({}, options);
    }
    if (argOptions.parentLogger) {
        // A child logger!
        return getLogger(`${argOptions.parentLogger.getName()} > ${loggerName}`, Object.assign(Object.assign(Object.assign({}, argOptions.parentLogger.getOptions()), argOptions), { parentLogger: undefined }));
    }
    /* Local, instance specific logging level. We initialize this to undefined because
    `shouldLog` relies on this being undefined in order to decide whether or not a local
    logging level was ever set. */
    let localLevel;
    const localTags = LogTags();
    if (options === null || options === void 0 ? void 0 : options.serverRequest) {
        const { serverRequest } = options;
        localTags.updateTags({
            caller_client_id: getCallerClientID(serverRequest),
            method: serverRequest.method,
            endpoint: serverRequest.originalUrl,
            request_id: serverRequest.requestId,
        });
    }
    return {
        log: (...messages) => logInner({
            level: Level.info,
            localLevel,
            messages,
            loggerName,
            tags: localTags.getTags(),
        }),
        error: (...messages) => logInner({
            level: Level.error,
            localLevel,
            messages,
            loggerName,
            tags: localTags.getTags(),
        }),
        warn: (...messages) => logInner({
            level: Level.warn,
            localLevel,
            messages,
            loggerName,
            tags: localTags.getTags(),
        }),
        debug: (...messages) => logInner({
            level: Level.debug,
            localLevel,
            messages,
            loggerName,
            tags: localTags.getTags(),
        }),
        info: (...messages) => logInner({
            level: Level.info,
            localLevel,
            messages,
            loggerName,
            tags: localTags.getTags(),
        }),
        getName: () => loggerName,
        getOptions: () => argOptions,
        setLoggingLevel: (newLevel) => {
            localLevel = newLevel;
        },
        addTag: (tag, value) => {
            localTags.addTag(tag, value);
        },
        updateTags: (tags) => {
            localTags.updateTags(tags);
        },
        setTags: (tags) => {
            localTags.setTags(tags);
        },
        clearTags: () => {
            localTags.clearTags();
        },
        clearLoggingLevel: () => {
            localLevel = undefined;
        },
    };
};
